/**
 * Pure placement math for the element-anchored coach popover
 * (coach-tooltip-system.md §4.2 / Phase 3a). Given an anchor's viewport rect, the
 * popover's measured size, and the viewport, decide which SIDE the popover sits on
 * (collision flipping) and its `position:fixed` top/left — clamped to stay fully on
 * screen. Extracted as a pure function (no DOM) so the geometry is the real setpoint
 * the SE asserts numerically (SE-14), mirroring `fabMenuHeight`'s computeFabMenuMaxHeightPx.
 *
 * The renderer (`CoachLayer`) supplies the live rects from getBoundingClientRect +
 * window.inner{Width,Height} and applies the returned coords; it owns NO geometry.
 */

export type PopoverSide = 'top' | 'bottom' | 'left' | 'right';

export interface RectLike {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}
export interface SizeLike {
  width: number;
  height: number;
}
export interface ViewportLike {
  width: number;
  height: number;
}
export interface Placement {
  side: PopoverSide;
  top: number;
  left: number;
}

const clamp = (v: number, min: number, max: number): number =>
  Math.max(min, Math.min(v, max));

/**
 * @param anchor   the target element's viewport rect (getBoundingClientRect)
 * @param popover  the popover's measured size
 * @param viewport the viewport size (window.innerWidth/innerHeight)
 * @param opts     gap = distance between anchor and popover; margin = min inset from viewport edges
 * @returns the chosen side + integer `position:fixed` top/left, always fully clamped on-screen.
 *
 * Side selection prefers `bottom`, then `top`, then `right`, then `left` — the FIRST
 * side with enough free space wins (collision flip). If NO side fully fits (popover
 * larger than every gap), it falls back to the side with the MOST space. Coordinates
 * are then clamped so the popover never spills past the viewport margins, even in the
 * degenerate case where the popover is larger than the viewport (clamp max is floored
 * at `margin` so it never inverts).
 */
export function computePopoverPlacement(
  anchor: RectLike,
  popover: SizeLike,
  viewport: ViewportLike,
  opts: { gap?: number; margin?: number } = {},
): Placement {
  const { gap = 8, margin = 8 } = opts;

  // Free space on each side between the anchor edge and the viewport edge, less the gap.
  const space: Record<PopoverSide, number> = {
    bottom: viewport.height - anchor.bottom - gap,
    top: anchor.top - gap,
    right: viewport.width - anchor.right - gap,
    left: anchor.left - gap,
  };
  const need: Record<PopoverSide, number> = {
    bottom: popover.height,
    top: popover.height,
    right: popover.width,
    left: popover.width,
  };

  const order: PopoverSide[] = ['bottom', 'top', 'right', 'left'];
  let side = order.find((s) => space[s] >= need[s]);
  if (!side) {
    // No side fully fits → pick the side with the most available space.
    side = order.reduce((best, s) => (space[s] > space[best] ? s : best), order[0]);
  }

  const centerX = anchor.left + anchor.width / 2;
  const centerY = anchor.top + anchor.height / 2;

  let top: number;
  let left: number;
  if (side === 'bottom') {
    top = anchor.bottom + gap;
    left = centerX - popover.width / 2;
  } else if (side === 'top') {
    top = anchor.top - gap - popover.height;
    left = centerX - popover.width / 2;
  } else if (side === 'right') {
    left = anchor.right + gap;
    top = centerY - popover.height / 2;
  } else {
    left = anchor.left - gap - popover.width;
    top = centerY - popover.height / 2;
  }

  // Clamp fully within the viewport margins. The max is floored at `margin` so that a
  // popover wider/taller than the viewport still produces min===max===margin (no inversion).
  left = clamp(left, margin, Math.max(margin, viewport.width - popover.width - margin));
  top = clamp(top, margin, Math.max(margin, viewport.height - popover.height - margin));

  return { side, top: Math.round(top), left: Math.round(left) };
}
